/** @jsxImportSource @emotion/react */
import { Skeleton, cn } from '@endure/ui';
import MuiTable from '@mui/material/Table';
import MuiTableBody from '@mui/material/TableBody';
import MuiTableCell from '@mui/material/TableCell';
import MuiTableContainer from '@mui/material/TableContainer';
import MuiTableRow from '@mui/material/TableRow';
import { Fragment, useMemo, useState } from 'react';
import { Link } from 'react-router';

import { useFormatTo } from 'hooks/useFormatTo';

import { Card } from 'components/Card';
import { useBreakpointUp } from 'hooks/responsive';
import Head from './Head';
import { TableCards } from './TableCards';
import { useStyles } from './styles';
import type { Order, TableColumn, TableProps } from './types';

export * from './types';

const SKELETON_ROW_COUNT = 6;
const SKELETON_COLUMN_WIDTHS = ['w-16', 'w-12', 'w-20', 'w-14', 'w-16'];

export function Table<R>({
  controls = true,
  columns,
  cardClassName,
  cardColumns,
  data,
  title,
  minWidth,
  initialOrder,
  rowOnClick,
  getRowHref,
  rowKeyExtractor,
  breakpoint,
  isFetching,
  size = 'md',
  header,
  placeholder,
  selectVariant,
  cellHeight,
  variant,
  tableLayout = 'fixed',
  renderRowFooter,
  renderRowControl,
  ...otherProps
}: TableProps<R>) {
  const styles = useStyles({ cellHeight });
  const { formatTo } = useFormatTo();
  const totalColumns = columns.length + (renderRowControl ? 1 : 0);

  // The fallback breakpoint is just to satisfy TS here, it is not actually used
  const _isBreakpointUp = useBreakpointUp(breakpoint || '2xl');
  const isBreakpointUp = !!breakpoint && _isBreakpointUp;

  const [order, setOrder] = useState<Order<R> | undefined>(initialOrder);

  const onRequestOrder = (column: TableColumn<R>) => {
    let newOrderDirection: 'asc' | 'desc' = 'desc';

    if (column.key === order?.orderBy.key) {
      newOrderDirection = order?.orderDirection === 'asc' ? 'desc' : 'asc';
    }

    setOrder({
      orderBy: column,
      orderDirection: newOrderDirection,
    });
  };

  const sortedData = useMemo(() => {
    if (!order || !order.orderBy.sortRows) {
      return data;
    }

    return [...data].sort((rowA, rowB) =>
      order.orderBy.sortRows!(rowA, rowB, order.orderDirection),
    );
  }, [data, order]);

  // First page load (fetching with nothing to show yet) renders layout-true
  // skeleton rows; a refetch that already has data keeps the rows and just dims
  // the body so there is no spinner flash or layout jump.
  const isFirstLoad = !!isFetching && data.length === 0;
  const isRefetching = !!isFetching && data.length > 0;

  const skeletonBody = (
    <MuiTableBody aria-hidden="true">
      {Array.from({ length: SKELETON_ROW_COUNT }).map((_, rowIndex) => (
        <MuiTableRow
          key={`skeleton-row-${rowIndex}`}
          css={styles.getTableRow({ clickable: false, rounded: variant === 'secondary' })}
        >
          {columns.map((column, columnIndex) => (
            <MuiTableCell css={styles.cellWrapper} key={column.key} align={column.align}>
              {columnIndex === 0 ? (
                <div className="flex items-center gap-3">
                  <Skeleton variant="circle" className="h-8 w-8 shrink-0" />
                  <Skeleton className="h-4 w-20" />
                </div>
              ) : (
                <div
                  className={cn(
                    'flex items-center',
                    column.align === 'right' && 'justify-end',
                    column.align === 'center' && 'justify-center',
                  )}
                >
                  <Skeleton
                    className={cn(
                      'h-4',
                      SKELETON_COLUMN_WIDTHS[columnIndex % SKELETON_COLUMN_WIDTHS.length],
                    )}
                  />
                </div>
              )}
            </MuiTableCell>
          ))}

          {renderRowControl && (
            <MuiTableCell className="align-middle">
              <Skeleton variant="circle" className="h-8 w-8" />
            </MuiTableCell>
          )}
        </MuiTableRow>
      ))}
    </MuiTableBody>
  );

  return (
    <Card css={styles.getRoot({ breakpoint })} aria-busy={isFetching || undefined} {...otherProps}>
      {title && (
        <div css={styles.getTitle({ breakpoint })} className="text-lg h-8">
          {title}
        </div>
      )}

      {!!header && (
        <div
          css={styles.getHeader({ breakpoint })}
          className={cn('mb-4', isBreakpointUp && 'mb-0')}
        >
          {header}
        </div>
      )}

      {data.length > 0 || isFetching || !placeholder ? (
        <>
          <MuiTableContainer css={styles.getTableContainer({ breakpoint })}>
            <MuiTable css={styles.table({ minWidth: minWidth ?? '0', tableLayout })}>
              <Head
                className={cn(variant === 'primary' && 'border-b border-dark-blue-hover')}
                controls={controls}
                columns={columns}
                orderBy={order?.orderBy}
                orderDirection={order?.orderDirection}
                onRequestOrder={onRequestOrder}
                rowControlColumn={!!renderRowControl}
              />

              {isFirstLoad && skeletonBody}

              <MuiTableBody
                className={cn('transition-opacity duration-250', isRefetching && 'opacity-60')}
              >
                {sortedData.map((row, rowIndex) => {
                  const rowKey = rowKeyExtractor(row);
                  const rowFooter = renderRowFooter?.(row, rowIndex);

                  const rowHref = getRowHref ? formatTo({ to: getRowHref(row) }) : undefined;
                  const handleRowClick = rowOnClick
                    ? (e: React.MouseEvent<HTMLTableRowElement>) => rowOnClick(e, row)
                    : undefined;

                  return (
                    <Fragment key={rowKey}>
                      <MuiTableRow
                        css={[
                          styles.link,
                          styles.getTableRow({
                            clickable: !!getRowHref || !!rowOnClick,
                            rounded: variant === 'secondary',
                          }),
                        ]}
                        onClick={handleRowClick}
                      >
                        {columns.map((column, columnIndex) => {
                          const cellContent = column.renderCell(row, rowIndex);
                          const cellTitle =
                            typeof cellContent === 'string' ? cellContent : undefined;

                          // Wrap per-cell, not per-row: <a> inside <td> is valid but <a> wrapping
                          // <tr> is not — row-level wrapping re-triggers the PR #91 hydration
                          // warning. <Link> restores native middle/cmd/right-click + screen-reader
                          // URL. First cell only gets tabIndex=0 for one tab stop per row.
                          const wrappedContent = rowHref ? (
                            <Link
                              to={rowHref}
                              tabIndex={columnIndex === 0 ? 0 : -1}
                              className="flex h-full w-full flex-col justify-center text-inherit no-underline"
                            >
                              {cellContent}
                            </Link>
                          ) : (
                            cellContent
                          );

                          return (
                            <MuiTableCell
                              css={styles.cellWrapper}
                              key={`${rowKey}-${column.key}`}
                              title={cellTitle}
                              align={column.align}
                            >
                              {wrappedContent}
                            </MuiTableCell>
                          );
                        })}

                        {renderRowControl && (
                          <MuiTableCell className="align-middle">
                            {renderRowControl(row, rowIndex)}
                          </MuiTableCell>
                        )}
                      </MuiTableRow>

                      {rowFooter !== undefined && rowFooter !== null && (
                        <MuiTableRow>
                          <MuiTableCell colSpan={totalColumns} sx={{ p: 0 }}>
                            {rowFooter}
                          </MuiTableCell>
                        </MuiTableRow>
                      )}
                    </Fragment>
                  );
                })}
              </MuiTableBody>
            </MuiTable>
          </MuiTableContainer>

          <TableCards
            controls={controls}
            cardClassName={cardClassName}
            selectVariant={selectVariant}
            data={sortedData}
            isFetching={isFetching}
            rowKeyExtractor={rowKeyExtractor}
            rowOnClick={rowOnClick}
            getRowHref={getRowHref}
            columns={cardColumns || columns}
            breakpoint={breakpoint}
            order={order}
            onOrderChange={setOrder}
            renderRowControl={renderRowControl}
            renderRowFooter={renderRowFooter}
          />
        </>
      ) : (
        placeholder
      )}
    </Card>
  );
}
